import { ReactElement } from 'react'

import { FormattedMessage } from 'react-intl'
import { useRouter } from 'solito/router'

import Box from 'app/components/base/Box'
import Button from 'app/components/base/Button'
import Stack from 'app/components/base/Stack'
import Text, { TextProps } from 'app/components/base/Text'
import useOpenExternalLink from 'app/hooks/useOpenExternalLink'
import useAppStore from 'app/store/app.store'
import { IS_NATIVE_APP, WEBSITE_URL } from 'app/utils/constants/env.constants'

const textStyles: TextProps['sx'] = {
  color: 'common.white',
  fontSize: IS_NATIVE_APP ? 28 : { xs: 28, sm: 48 },
  lineHeight: IS_NATIVE_APP ? 48 : { xs: '48px', sm: '65px' }
}

interface NeedHelpProps {
  icons: ReactElement
}

const NeedHelp: RFC<NeedHelpProps> = ({ icons }) => {
  const { push } = useRouter()
  const { openLink } = useOpenExternalLink()
  const isTablet = useAppStore((state) => state.isTablet)

  const handleClick = () => {
    const link = '/help-centre'

    if (IS_NATIVE_APP) {
      openLink({ link: `${WEBSITE_URL}/${link}`, title: 'Help Centre' })
      return
    }

    push(link)
  }

  return (
    <Box
      sx={{
        py: { xs: 6, md: 8.75 },
        backgroundColor: 'primary.main',
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        overflowX: 'hidden'
      }}
    >
      <Stack
        direction="column"
        spacing={2}
        sx={{
          px: { xs: 2, md: 5 },
          width: { xs: '100%', lg: isTablet ? '100%' : 1200 }
        }}
      >
        <Stack direction="column" sx={{ color: 'background.default' }}>
          <Text variant="h1" component="h3" sx={{ ...textStyles }}>
            <FormattedMessage defaultMessage="Need help?" />
          </Text>

          <Text variant="h2" component="h3" sx={{ ...textStyles }}>
            <FormattedMessage defaultMessage="Check out our help centre" />
          </Text>
        </Stack>

        <Button
          aria-label="go to help centre"
          variant="contained"
          color="secondary"
          sx={{
            width: 190,
            color: 'primary.main',
            ...(IS_NATIVE_APP && { flex: 1 })
          }}
          onClick={handleClick}
        >
          <FormattedMessage defaultMessage="Go to Help Centre" />
        </Button>
      </Stack>

      <Box
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          top: '50%',
          right: { lg: 40, xl: 120, xxl: 240 },
          ...(!IS_NATIVE_APP && { transform: 'translateY(-50%)' }),
          ...(isTablet && { right: { xs: 2, lg: 5 } })
        }}
      >
        {icons}
      </Box>
    </Box>
  )
}

export default NeedHelp
